// VIDEO-EDITING — transient store for Marcus's overlay proposals.
//
// `propose_video_overlays` (backend dry-run sibling of
// `upsert_video_overlays`) returns a normalized overlay plan without
// persisting. `useCompanionToolDispatch` lifts that result here so
// `MarcusSuggestionsPanel` can render proposal cards for the user to
// accept / edit / discard before a follow-up `upsert_video_overlays`
// commits the approved subset.
//
// State machine:
//   idle ──Generate──► generating ──propose result──► proposed
//                          │                           │
//                          └── 90s timeout ──► error   │ Apply
//                                                      ▼
//                                                  applying
//                                          upsert result │
//                                                      ▼
//                                                   applied (10s)
//                                                      │ auto
//                                                      ▼
//                                                    idle  ◄── Undo (60s)
//
// Intentionally NOT persisted — proposals + lastApplied are
// session-scoped. `lastApplied.previousOverlays` is the snapshot we
// took at Apply time so Undo can re-upsert the prior group.
import { create } from "zustand";

import type { Overlay } from "../types";

export type OverlayProposalsStatus =
  | "idle"
  | "generating"
  | "proposed"
  | "applying"
  | "applied"
  | "error";

interface LastAppliedSnapshot {
  projectId: string;
  sourceActionId: string;
  /** overlays now live on the project (post-apply) */
  appliedOverlays: Overlay[];
  /** snapshot of the same-sourceActionId group BEFORE the apply — used by Undo */
  previousOverlays: Overlay[];
  /** ids the upsert call replaced (subset of previousOverlays) */
  replacedIds: string[];
  /** epoch ms — used by the 60s lastApplied window in idle */
  at: number;
}

interface OverlayProposalsState {
  status: OverlayProposalsStatus;
  projectId: string | null;
  sourceActionId: string | null;
  roles: string[] | null;
  proposals: Overlay[];
  /** ids of existing overlays the apply step would replace */
  wouldReplaceOverlayIds: string[];
  /** total project duration if the propose tool surfaced it, for time-range bounds */
  durationSec: number | null;
  /** transient error message from the last propose / apply run */
  error: string | null;
  /** populated on successful apply; cleared by clearLastApplied or by starting a new propose */
  lastApplied: LastAppliedSnapshot | null;
  /**
   * When set, the next propose result replaces ONLY this single card
   * (matched by role) instead of the whole proposals array. Cleared
   * after the dispatch lands or by a 60s safety timer in the panel.
   */
  regeneratingCardId: string | null;

  beginGenerating: (projectId: string) => void;
  setProposals: (input: {
    projectId: string;
    sourceActionId: string | null;
    roles: string[] | null;
    proposals: Overlay[];
    wouldReplaceOverlayIds: string[];
    durationSec: number | null;
  }) => void;
  setError: (projectId: string, error: string) => void;
  updateProposal: (id: string, patch: Partial<Overlay>) => void;
  removeProposal: (id: string) => void;
  clearProposals: () => void;

  /** flip to applying state; snapshot the prior overlays so Undo can restore them */
  beginApplying: (input: { projectId: string; previousOverlays: Overlay[] }) => void;
  /** dispatch sets this when `video_overlays_upserted` lands */
  markApplied: (input: {
    projectId: string;
    sourceActionId: string;
    appliedOverlays: Overlay[];
    replacedIds: string[];
  }) => void;
  /** dispatch sets this when `video_overlays_upsert_error` lands */
  markApplyError: (projectId: string, error: string) => void;
  /** invoked by the "Undo" CTA — returns the snapshot so the panel can fire the prompt */
  consumeLastApplied: () => LastAppliedSnapshot | null;
  /** invoked by the auto-expiry timer or when a new propose run starts */
  clearLastApplied: () => void;

  /** flag a single card as being regenerated. Panel uses this to render a spinner. */
  beginRegeneratingCard: (cardId: string) => void;
  /** clear regeneratingCardId — call from the dispatch + safety timer */
  endRegeneratingCard: () => void;
  /** atomically replace a single card in proposals (used when the per-card propose lands) */
  replaceProposalById: (cardId: string, replacement: Overlay) => void;
}

export const useOverlayProposalsStore = create<OverlayProposalsState>((set, get) => ({
  status: "idle",
  projectId: null,
  sourceActionId: null,
  roles: null,
  proposals: [],
  wouldReplaceOverlayIds: [],
  durationSec: null,
  error: null,
  lastApplied: null,
  regeneratingCardId: null,

  beginGenerating: (projectId) =>
    set({
      status: "generating",
      projectId,
      proposals: [],
      wouldReplaceOverlayIds: [],
      error: null,
      // a new propose run invalidates the prior undo window — once we
      // surface fresh proposals the snapshot is effectively stale
      lastApplied: null,
    }),

  setProposals: ({ projectId, sourceActionId, roles, proposals, wouldReplaceOverlayIds, durationSec }) =>
    set({
      status: proposals.length > 0 ? "proposed" : "idle",
      projectId,
      sourceActionId,
      roles,
      proposals,
      wouldReplaceOverlayIds,
      durationSec,
      error: null,
    }),

  setError: (projectId, error) =>
    set({
      status: "error",
      projectId,
      proposals: [],
      wouldReplaceOverlayIds: [],
      error,
    }),

  updateProposal: (id, patch) =>
    set((state) => ({
      proposals: state.proposals.map((overlay) =>
        overlay.id === id ? { ...overlay, ...patch } : overlay,
      ),
    })),

  removeProposal: (id) =>
    set((state) => {
      const next = state.proposals.filter((overlay) => overlay.id !== id);
      return {
        proposals: next,
        status: next.length === 0 ? "idle" : state.status,
      };
    }),

  clearProposals: () =>
    set({
      status: "idle",
      proposals: [],
      wouldReplaceOverlayIds: [],
      error: null,
    }),

  beginApplying: ({ projectId, previousOverlays }) =>
    set((state) => ({
      status: "applying",
      projectId,
      // stash previousOverlays on lastApplied early so even a crash
      // between Apply and the upsert result still preserves an Undo path
      lastApplied: {
        projectId,
        sourceActionId: state.sourceActionId ?? "hook-overlays",
        appliedOverlays: [],
        previousOverlays,
        replacedIds: [],
        at: Date.now(),
      },
    })),

  markApplied: ({ projectId, sourceActionId, appliedOverlays, replacedIds }) =>
    set((state) => {
      const previousOverlays = state.lastApplied?.previousOverlays ?? [];
      // KEEP proposals visible after Apply so the user can tweak text /
      // timing without re-running Generate. The store's `lastApplied`
      // snapshot is the source-of-truth for "what's live" — we compare
      // current proposals against it to detect unsaved post-apply
      // edits and surface the "Apply changes" CTA. Reset only the
      // transient propose-time fields (replace summary, error, generating).
      return {
        status: "applied",
        projectId,
        sourceActionId,
        wouldReplaceOverlayIds: [],
        error: null,
        lastApplied: {
          projectId,
          sourceActionId,
          appliedOverlays,
          previousOverlays,
          replacedIds,
          at: Date.now(),
        },
      };
    }),

  markApplyError: (projectId, error) =>
    set({
      status: "error",
      projectId,
      error,
    }),

  consumeLastApplied: () => {
    const snapshot = get().lastApplied;
    if (snapshot) set({ lastApplied: null, status: "idle" });
    return snapshot;
  },

  clearLastApplied: () => set({ lastApplied: null }),

  beginRegeneratingCard: (cardId) => set({ regeneratingCardId: cardId }),

  endRegeneratingCard: () => set({ regeneratingCardId: null }),

  replaceProposalById: (cardId, replacement) =>
    set((state) => ({
      proposals: state.proposals.map((overlay) =>
        overlay.id === cardId ? replacement : overlay,
      ),
      regeneratingCardId: null,
    })),
}));
